/**
 * Cliente HTTP para as APIs do MGC persistente (Bloco 8).
 * Automaticamente injeta o JWT Supabase e trata erros comuns.
 * Design: nunca lança para o caller — sempre retorna { ok, data, error }
 * para que os stores possam decidir entre modo API e modo offline (localStorage).
 */
import axios from "axios";
import { getAccessToken, getSupabase } from "@/lib/supabase";
import { impHeaders } from "@/lib/impersonation";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Phase 12.55.29 — Poll de até ~3 s aguardando o Bearer estar disponível.
// Impede o "sem-auth" espúrio em telas que abrem imediatamente após
// login, quando o cliente Supabase ainda está rehidratando a sessão.
async function authHeaders() {
  let token = await getAccessToken();
  if (!token) {
    for (let i = 0; i < 6 && !token; i++) {
      await new Promise(r => setTimeout(r, 500));
      try { token = await getAccessToken(); } catch { token = null; }
    }
  }
  if (!token) return null;
  return { Authorization: `Bearer ${token}`, ...impHeaders() };
}

async function safeRequest(method, path, data = null) {
  const headers = await authHeaders();
  if (!headers) return { ok: false, reason: "no-auth", error: "sem-auth", offline: true };
  const doCall = async (h) => axios({
    method,
    url: `${API_BASE}${path}`,
    headers: { ...h, "Content-Type": "application/json" },
    data,
    timeout: 20000,
  });
  try {
    const resp = await doCall(headers);
    return { ok: true, data: resp.data };
  } catch (e) {
    const status = e?.response?.status;
    // Retry em 401: força refresh + reenvia.
    if (status === 401) {
      try {
        const sb = await getSupabase();
        const { data: refreshed } = await sb.auth.refreshSession();
        const newTok = refreshed?.session?.access_token || null;
        if (newTok) {
          try {
            const resp2 = await doCall({ Authorization: `Bearer ${newTok}`, ...impHeaders() });
            return { ok: true, data: resp2.data };
          } catch (e2) {
            const st2 = e2?.response?.status;
            const dt2 = e2?.response?.data?.detail || e2.message;
            if (st2 === 401 || st2 === 403) return { ok: false, reason: "no-auth", error: dt2, status: st2, offline: true };
            if (!st2 || st2 >= 500)          return { ok: false, reason: "server-down", error: dt2, status: st2, offline: true };
            return { ok: false, reason: "api-error", error: dt2, status: st2, offline: false };
          }
        }
      } catch { /* refresh silencioso — cai no fluxo padrão */ }
    }
    const detail = e?.response?.data?.detail || e.message;
    if (status === 401 || status === 403) {
      return { ok: false, reason: "no-auth", error: detail, status, offline: true };
    }
    if (!status || status >= 500) {
      return { ok: false, reason: "server-down", error: detail, status, offline: true };
    }
    return { ok: false, reason: "api-error", error: detail, status, offline: false };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Itens
// ─────────────────────────────────────────────────────────────────────────────
export function apiListarItens(area = null) {
  const qs = area ? `?area=${encodeURIComponent(area)}` : "";
  return safeRequest("GET", `/api/mgc/itens${qs}`);
}

export function apiCriarItem(payload) {
  return safeRequest("POST", `/api/mgc/itens`, payload);
}

export function apiMoverItem(id, targetArea, extra = null, motivo = null) {
  return safeRequest("PATCH", `/api/mgc/itens/${id}/mover`, {
    target_area: targetArea,
    extra,
    motivo,
  });
}

export function apiTimeline(id) {
  return safeRequest("GET", `/api/mgc/itens/${id}/eventos`);
}

// ─────────────────────────────────────────────────────────────────────────────
// Parâmetros governados (Bloco 11 persistente)
// ─────────────────────────────────────────────────────────────────────────────
export function apiParametrosAtivos() {
  return safeRequest("GET", `/api/mgc/parametros`);
}

// Plano Customizado (B3) — versão vigente de UM motor específico (global inclusive).
export function apiParametrosAtivosMotor(motor) {
  return safeRequest("GET", `/api/mgc/parametros?motor=${encodeURIComponent(motor)}`);
}

// `publicacoes`: [{ motor, valores, diff }] — uma versão por motor alterado,
// gravadas em transação única no servidor (tudo ou nada).
export function apiPublicarParametros(publicacoes, justificativa, vigenciaEm = null, escopoGlobal = false) {
  return safeRequest("POST", `/api/mgc/parametros/publicar`, {
    publicacoes, justificativa,
    vigencia_em: vigenciaEm,
    escopo_global: escopoGlobal,
  });
}

export function apiVersoes() {
  return safeRequest("GET", `/api/mgc/parametros/versoes`);
}

// ─────────────────────────────────────────────────────────────────────────────
// Leitura da Val (Adendo v1.0)
// ─────────────────────────────────────────────────────────────────────────────
export function apiLdvMes() {
  return safeRequest("GET", `/api/mgc/leitura-val/mes`);
}

export function apiLdvVersoes() {
  return safeRequest("GET", `/api/mgc/leitura-val/versoes`);
}

export function apiLdvVersao(id) {
  return safeRequest("GET", `/api/mgc/leitura-val/versoes/${encodeURIComponent(id)}`);
}

/**
 * Chama Claude para gerar nova leitura. Timeout estendido (60s) porque
 * uma geração completa pode levar 20-30s.
 *
 * Retornos possíveis:
 *   { ok:true, data }                                     — sucesso
 *   { ok:false, reason:"no-auth", offline:true }          — sessão ausente
 *   { ok:false, reason:"server-down", offline:true, ... } — 5xx / rede
 *   { ok:false, reason:"api-error", offline:false, ... }  — 4xx (validação)
 */
export async function apiLdvGerar(geradoPor = "manual") {
  const headers = await authHeaders();
  if (!headers) return { ok: false, reason: "no-auth", offline: true, error: "sem-auth" };
  try {
    const resp = await axios.post(
      `${API_BASE}/api/mgc/leitura-val/gerar`,
      { gerado_por: geradoPor },
      { headers: { ...headers, "Content-Type": "application/json" }, timeout: 60000 },
    );
    return { ok: true, data: resp.data };
  } catch (e) {
    const status = e?.response?.status;
    const detail = e?.response?.data?.detail || e.message;
    if (status === 401 || status === 403) {
      return { ok: false, reason: "no-auth", offline: true, error: detail, status };
    }
    if (!status || status >= 500) {
      return { ok: false, reason: "server-down", offline: true, error: detail, status };
    }
    return { ok: false, reason: "api-error", offline: false, error: detail, status };
  }
}

/**
 * Inicia Motor V360 + Val em background (retorna job_id imediatamente, HTTP 202).
 * Sem risco de 502 por gateway timeout — o processamento pesado é assíncrono.
 */
export async function apiRodarCompletoAsync(geradoPor = "manual") {
  const headers = await authHeaders();
  if (!headers) return { ok: false, reason: "no-auth", offline: true, error: "sem-auth" };
  try {
    const resp = await axios.post(
      `${API_BASE}/api/mgc/v360/motor/rodar-completo-async`,
      { gerado_por: geradoPor },
      { headers: { ...headers, "Content-Type": "application/json" }, timeout: 15000 },
    );
    return { ok: true, data: resp.data }; // { job_id, status: "running", phase: "motor" }
  } catch (e) {
    const status = e?.response?.status;
    const detail = e?.response?.data?.detail || e.message;
    if (status === 401 || status === 403) return { ok: false, reason: "no-auth", offline: true, error: detail, status };
    if (!status || status >= 500) return { ok: false, reason: "server-down", offline: true, error: detail, status };
    return { ok: false, reason: "api-error", offline: false, error: detail, status };
  }
}

/**
 * Consulta o status de um job assíncrono Motor+Val.
 * Resposta: { status: "running"|"done"|"error", phase: str, result, error }
 */
export async function apiPollJob(jobId) {
  const headers = await authHeaders();
  if (!headers) return { ok: false, reason: "no-auth" };
  try {
    const resp = await axios.get(
      `${API_BASE}/api/mgc/v360/motor/job/${jobId}`,
      { headers, timeout: 10000 },
    );
    return { ok: true, data: resp.data };
  } catch (e) {
    const status = e?.response?.status;
    if (status === 404) return { ok: false, reason: "not-found", status };
    return { ok: false, reason: "transient", status };
  }
}


/**
 * Bloco 3.3 — Transforma um bullet da Leitura da Val em recomendação
 * completa (via motor E7 · Claude Sonnet 4.6). Idempotente por
 * (versao_id, bullet_id): 2ª chamada retorna `reused=true` sem re-invocar
 * a IA. Timeout estendido (60s) porque a expansão pode levar 15-30s.
 *
 * Retornos:
 *   { ok:true, data: { reused, mgc_item_id, recomendacao, regra_id, modelo_llm, latencia_ms } }
 *   { ok:false, reason, error, status }
 */
export async function apiLdvTransformarBullet(versaoId, bulletId, bulletTipo) {
  const headers = await authHeaders();
  if (!headers) return { ok: false, reason: "no-auth", error: "sem-auth" };
  try {
    const resp = await axios.post(
      `${API_BASE}/api/mgc/leitura-val/transformar-bullet`,
      { versao_id: versaoId, bullet_id: bulletId, bullet_tipo: bulletTipo },
      { headers: { ...headers, "Content-Type": "application/json" }, timeout: 60000 },
    );
    return { ok: true, data: resp.data };
  } catch (e) {
    const status = e?.response?.status;
    const detail = e?.response?.data?.detail || e.message;
    if (status === 401 || status === 403) return { ok: false, reason: "no-auth", error: detail, status };
    if (status === 404)                     return { ok: false, reason: "not-found", error: detail, status };
    if (!status || status >= 500)           return { ok: false, reason: "server-down", error: detail, status };
    return { ok: false, reason: "api-error", error: detail, status };
  }
}
